/**
 * The route table (`CR-006`, `ARCH-001 §4`).
 *
 * **This is the only file in the app that contains a path string.** A `<Link>` or a `navigate()` refers to
 * a route by name, so moving a screen is a one-line change here rather than a search — and a typo is a
 * TypeScript error rather than a link that quietly 404s.
 *
 * Every route outside the initial set is a `lazy()` boundary (`STD-002 §8`), which is what makes the
 * splitting in `ARCH-005 §2.1` possible at all. `Home`, `Ledger` and `Add Entry` are the exceptions: they
 * are the first thing anyone sees and loading them on demand would only move the wait.
 */

/** Every route this app has, by name. Add a screen by adding a member and an entry to `routePaths`. */
export type RouteName =
  | 'home'
  | 'addEntry'
  | 'ledger'
  | 'trash'
  | 'entry'
  | 'welcome'
  | 'createAccount'
  | 'verifyEmail'
  | 'signIn'
  | 'resetPassword'
  | 'region'
  | 'storageMode'
  | 'trialStarted'
  | 'notFound';

/**
 * Name to path. Frozen so that nothing can rewrite a route at runtime, which would make the table a lie
 * for anything that read it earlier.
 */
export const routePaths = Object.freeze({
  home: '/',

  /**
   * `LED-02`. In the initial chunk with Home, per `STD-002 §8`: it is reached from the `+` on every ledger
   * screen and is the thing the product is *for*, so a chunk fetched on the first tap would put a network
   * round trip between somebody and the five seconds this is supposed to take.
   */
  addEntry: '/add',

  /**
   * `LED-05`. In the initial chunk with Home and Add Entry (`STD-002 §8`) — it is one of the four nav tabs
   * and the one people move to and from most.
   */
  ledger: '/ledger',

  /** `LED-07`. Reached from settings and from the undo toast, rarely — so it is behind a `lazy()`. */
  trash: '/ledger/deleted',

  /**
   * `LED-04`. The entry's public id is in the path, and that is safe in a way an address is not: it names
   * a row that already requires this person's session to read, and `Ownership.Require` answers somebody
   * else's with the same `404` as one that never existed (`ARCH-003 §2` rule 2).
   */
  entry: '/entry/:id',

  /**
   * `ONB-01`, the first screen a signed-out person sees. It is a route of its own rather than the root so
   * that the root can become "wherever this person belongs" when there is a session to ask (`T-0120`).
   */
  welcome: '/welcome',

  /** `ONB-02`. */
  createAccount: '/create-account',

  /**
   * `ONB-03`. The address being verified is carried in the router's location state, never in the path —
   * a path is written into history, into a referrer and into every access log on the way (`ARCH-003 §6`).
   */
  verifyEmail: '/verify-email',

  /** `ONB-04`. */
  signIn: '/sign-in',

  /**
   * The two steps behind `ONB-04`'s "Forgot password?" — asking for a code and spending it. One route,
   * because the person leaves for their inbox and comes back, and coming back to the address they typed
   * is better than coming back to a form asking for it again (`FEAT-005 §2`).
   */
  resetPassword: '/reset-password',

  /** `ONB-05`, step 1 of 2. */
  region: '/setup/region',

  /** `ONB-06`, step 2 of 2. */
  storageMode: '/setup/storage',

  /** `ONB-07`. The trial's end date is handed across in location state, or read from `GET /me`. */
  trialStarted: '/setup/done',

  /**
   * React Router's catch-all. It is a path in the table like any other so that nothing outside this file
   * has to know the `*` convention.
   */
  notFound: '*',
} as const satisfies Record<RouteName, string>);

/**
 * The path for a route. Everything that navigates goes through this rather than typing a literal.
 *
 * It takes no parameters yet because no route has any; when one does, the signature changes here and every
 * caller that needs updating stops compiling, which is the point of the indirection.
 */
export function pathTo(route: RouteName): string {
  return routePaths[route];
}

/**
 * The path for a route that names one thing — today, only `LED-04`.
 *
 * Separate from `pathTo` rather than a second optional argument, so that forgetting the id is a compile
 * error rather than a link to a literal `:id`.
 */
export function pathToEntry(id: string): string {
  return routePaths.entry.replace(':id', encodeURIComponent(id));
}
